import { useState } from 'react';
import styled from 'styled-components';

import Button from 'components/shared/Button';
import FieldLabel from 'components/shared/form/FieldLabel';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import AttrValue from 'models/AttrValue';
import Workflow from 'models/Workflow';
import { WorkflowAction } from 'models/WorkflowAction';
import { useInfiniteAttrValues } from 'services/api/attrValue';
import { WorkflowValuesInput } from 'services/api/workflows';
import { FONT_WEIGHT_MEDIUM } from 'styles/theme/textStyles';
import { BodySmall } from 'styles/typography';

import { REFERRAL_SOURCE_SELECT_STYLES, VALUE_NAMES } from './constants';

/**
 * The two states this step asks in. Either the referral named a source we don't recognize, or it
 * named none at all (OLIO-3302), and reporting has nowhere to file it until someone says which of
 * the client's own names it belongs under.
 *
 * The panel is an exception by design. Anything the client already has a name for resolves without
 * asking and the step parks unseen, which is why this is worth a moment when it does appear, and
 * why it asks for exactly one thing and nothing else.
 *
 * Answering is per referral and nothing else. It used to also record the wording as a standing
 * match, which is what OLIO-3302 removed: an AI misreading of one packet decided every referral
 * worded the same way. Aliases are an admin's to manage now.
 */

// What the API records on the action while it waits: one entry per value it couldn't place.
//
// Deliberately NOT the list of choices. The API sends one, built when the step first asked, and
// reading it is what OLIO-3158 shipped with: a snapshot frozen at that moment, so a referral
// source an admin added while the question sat open never appeared in the picker (QA, Sep 1).
// The choices are the client's configuration rather than evidence about this referral, and they
// can change while the card waits, so they are fetched below instead.
//
// `rawValue` is null when the referral named no source, which is the card's other state. The
// answer is keyed by `valueName` rather than by an echo of the wording, so a question with no
// wording is answerable at all.
export interface OutstandingSource {
  valueName: string;
  attrId: string;
  attrName: string;
  rawValue: string | null;
}

const NEW_SOURCE = '__new__';

// The "add one" row, shaped as an AttrValue so it can ride the same list as the real ones. Same
// trick ReferralModal uses for the referral's own stored wording. Module-level so its identity is
// stable — Select memoizes its option list on this array.
const ADD_NEW_OPTIONS = [{ id: NEW_SOURCE, name: 'Add a new referral source…' } as AttrValue];

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;
  max-width: 480px;
`;

// The packet's own wording, quoted rather than tidied: the coordinator is being asked to
// recognize it, and a cleaned-up version is a different string than the one on the document.
//
// A span, not styled(BodySmallBold). Body is a styled.p, so the bold variant is a <p> too, and
// nesting it inside the sentence's own <p> is invalid markup: the browser closed the outer
// paragraph early, which dropped the wording onto a line of its own and lost its weight.
const RawValue = styled.span`
  font-weight: ${FONT_WEIGHT_MEDIUM};
  word-break: break-word;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export default function ConfirmReferralSource({
  action,
  outstanding,
  onComplete,
  isCompleting,
}: {
  action: WorkflowAction;
  outstanding: OutstandingSource[];
  onComplete: (actionId: string, outputs: WorkflowValuesInput) => unknown;
  isCompleting: boolean;
}) {
  // One question at a time. The API contract allows several, but referral intake declares a single
  // value, so a form for N would be scaffolding for a case that does not exist.
  const question = outstanding[0];
  const [picked, setPicked] = useState<AttrValue | null>(null);
  const [typed, setTyped] = useState('');
  const { canEditAction } = useWorkflowPermissions();

  // The client's own referral sources, asked for when the coordinator opens the picker rather than
  // taken from the action's recorded question. The same hook and the same arguments the create
  // form's picker uses, so the two lists cannot disagree.
  //
  // `enabled: false` is not a mistake: Select's onMenuOpen pulls the first page, then typing
  // narrows and scrolling pages. Deduplicated by display name server-side, since several raw
  // wordings share one reporting name and a picker must offer it once.
  // `visible` matters here beyond tidiness: picking a retired name records a Raw Value pointing at
  // it, after which every future referral worded that way resolves to it silently.
  const referralSources = useInfiniteAttrValues({
    workflowValueName: VALUE_NAMES.REFERRAL_SOURCE,
    distinctDisplayName: true,
    active: true,
    visible: true,
  });

  if (!question) return null;

  const isNew = picked?.id === NEW_SOURCE;
  // `name` on this endpoint IS the reporting name — attr_values#index serializes display_name into
  // it — which is what the answer has to carry.
  const displayName = isNew ? typed.trim() : (picked?.name ?? '');

  const confirm = () => {
    if (!displayName) return;
    // camelCase on the way out too, which OliveBranch snake_cases before the API sees it. Sending
    // snake_case happens to survive the transform, but relying on that is how the read side broke.
    onComplete(action.id, {
      attrResolutions: [{ valueName: question.valueName, displayName }],
    });
  };

  return (
    <Panel data-testid='confirm-referral-source'>
      {/* Which sentence, and nothing else, is what the two states differ by. The wording is quoted
          rather than tidied: the coordinator is being asked to recognize it, and a cleaned-up
          version is a different string than the one on the document. */}
      {question.rawValue ? (
        <BodySmall>
          The referral packet listed the source as{' '}
          <RawValue data-testid='referral-source-raw'>“{question.rawValue}”</RawValue>
        </BodySmall>
      ) : (
        <BodySmall data-testid='referral-source-missing'>We weren’t able to identify a referral source.</BodySmall>
      )}

      {/* Everything from here down is the answering, and answering completes the action — which the API's policy
          refuses for a viewer. What stays above is the question itself: the wording the packet used, or that none was
          found. That is a fact about the referral and a reader should have it; the instruction to select one, the
          picker and Confirm are not. */}
      {!canEditAction(action) ? null : (
        <>
          {/* One line rather than the two it used to pick between. The old copy had a "you have no
              referral sources yet" variant, chosen from a count the payload carried; the list is not
              counted until the coordinator opens the picker now, and copy that rewrites itself on
              open is worse than copy that reads correctly either way. A client with none sees the
              one row that matters.

              What is NOT here any more is the second sentence, which promised that referrals worded
              this way would match automatically from then on. That was true when answering wrote an
              alias, and OLIO-3302 stopped it doing that. */}
          <BodySmall>Select which referral source on file this referral should be reported under.</BodySmall>

          <Field>
            <FieldLabel>Referral Source</FieldLabel>
            <Select<AttrValue>
              {...referralSources}
              appendedOptions={ADD_NEW_OPTIONS}
              value={picked}
              // Naming a new source starts from the wording the referral actually used, since that is
              // usually most of the answer — "ST MARYS HOSPITAL" wants tidying, not retyping. Only when
              // the box is empty, so switching away and back never overwrites what they typed.
              onChange={(option) => {
                setPicked(option);
                if (option?.id === NEW_SOURCE && !typed && question.rawValue) setTyped(question.rawValue);
              }}
              getOptionValue={(option) => option.id}
              getOptionLabel={(option) => option.name}
              placeholder='Select...'
              dataTestId='referral-source-choice'
              disabled={isCompleting}
              menuPortalTarget={document.body}
              menuPlacement='auto'
              maxMenuHeight={240}
              // The menu is portalled to <body> to escape the card's `overflow: clip` (categoryCard/
              // styles.ts rounds the tinted header and rows to the card corners, which would truncate an
              // inline menu). But a body-portalled menu also escapes the scroll COLUMN's clip, so as the
              // referral page's right column scrolls, react-select keeps the menu glued to the control
              // (floating-ui autoUpdate) and it ends up drawn over the header/tab strip (OLIO-3439).
              // Block page scroll while the menu is open so it stays put and anchored instead of being
              // scrolled over the header; the option list still scrolls internally (infinite paging).
              menuShouldBlockScroll
              styles={REFERRAL_SOURCE_SELECT_STYLES}
            />

            {isNew && (
              <Input
                value={typed}
                onChange={(event) => setTyped(event.target.value)}
                placeholder='Name this referral source'
                disabled={isCompleting}
                data-testid='referral-source-new'
              />
            )}
          </Field>

          <div>
            <Button
              variant='primary'
              size='small'
              disabled={!displayName || isCompleting}
              onClick={confirm}
              data-testid='referral-source-confirm'>
              Confirm
            </Button>
          </div>
        </>
      )}
    </Panel>
  );
}

/**
 * What the referral reports under, once someone has answered. The step completes now rather than
 * parking (OLIO-3302: the answer is the referral's own value, and MANUAL outranks anything the AI
 * or a portal reports later), and a completed row that rendered nothing would read as though the
 * question had simply vanished.
 *
 * Reads the VALUE, not the action's own outputs, which is what every other completed surface here
 * does (CategoryCard's CompletedResult). The outputs are frozen at completion and this kind can
 * never go stale or re-run, so a source changed afterwards through the referral's Edit would leave
 * this row asserting a name the referral no longer reports under, permanently. The value is the
 * live truth, and when it stops matching anything the row correctly says nothing.
 */
export function ConfirmedReferralSource({ workflow }: { workflow: Workflow }) {
  const confirmed = workflow.valueFor(VALUE_NAMES.REFERRAL_SOURCE)?.value;
  if (typeof confirmed !== 'string' || !confirmed) return null;

  return <BodySmall data-testid='referral-source-confirmed'>Reported under {confirmed}</BodySmall>;
}
